import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { AdminSectionTabs } from '@/components/admin/AdminSectionTabs';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ReaderAvatar } from '@/components/ui/ReaderAvatar';
import { StarRating } from '@/components/ui/StarRating';
import { reviewsService } from '@/services/reviews.service';
import { ApiError } from '@/services/http';
import type { AdminReviewView, ReviewCommentStatus } from '@/types/api';

/**
 * AdminReviewsPage — `/admin/reviews`. The moderation queue.
 *
 * Layout follows the rest of the admin shell: section tabs at the top,
 * sub-header (kicker + heading + counts), filter chips, then the
 * paginated table.
 *
 * Filter defaults to `pending` because that's the work-to-do view —
 * admins can flip to `approved`/`rejected` for audit. The table is
 * pre-sorted server-side to push pending rows to the top, so even
 * the "all statuses" filter (book-scoped lookups) shows pending first.
 */

const PAGE_SIZE = 25;

type StatusFilter = 'pending' | 'approved' | 'rejected';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

export function AdminReviewsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = (searchParams.get('status') as StatusFilter | null) ?? 'pending';
  const page = Math.max(1, Number(searchParams.get('page') ?? '1'));
  const offset = (page - 1) * PAGE_SIZE;

  const [reviews, setReviews] = useState<AdminReviewView[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminReviewView | null>(null);

  const lastPage = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reviewsService
      .adminList({ status, limit: PAGE_SIZE, offset })
      .then((page) => {
        if (cancelled) return;
        setReviews(page.reviews);
        setTotalCount(page.totalCount);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err instanceof ApiError ? err.message : 'Could not load reviews.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [status, offset]);

  const applyStatus = (next: StatusFilter) => {
    const params = new URLSearchParams(searchParams);
    params.set('status', next);
    params.set('page', '1');
    setSearchParams(params, { replace: true });
  };

  const goToPage = (next: number) => {
    const clamped = Math.max(1, Math.min(lastPage, next));
    const params = new URLSearchParams(searchParams);
    params.set('page', String(clamped));
    setSearchParams(params, { replace: true });
  };

  const handleModerate = async (
    review: AdminReviewView,
    decision: 'approved' | 'rejected',
  ) => {
    if (busyId) return;
    setBusyId(review.id);
    try {
      const updated = await reviewsService.adminModerate(review.id, decision);
      // After moderating, drop the row from the current view (it no
      // longer matches the filter) instead of mutating in place — the
      // count decrements visibly and the admin can blow through the
      // queue without context-switching.
      if (status !== decision) {
        setReviews((prev) => prev.filter((r) => r.id !== updated.id));
        setTotalCount((c) => Math.max(0, c - 1));
      } else {
        setReviews((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      }
      toast.success(decision === 'approved' ? 'Comment approved.' : 'Comment rejected.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update review.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    setBusyId(target.id);
    try {
      await reviewsService.adminRemove(target.id);
      setReviews((prev) => prev.filter((r) => r.id !== target.id));
      setTotalCount((c) => Math.max(0, c - 1));
      toast.success('Review deleted.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete review.');
    } finally {
      setBusyId(null);
    }
  };

  const statusCounts = useMemo(() => {
    return STATUS_FILTERS.map((f) => ({
      ...f,
      // The list only shows one status at a time, so the count chip on
      // the current filter mirrors `totalCount`; the others stay
      // unlabeled until the admin clicks them. Cheap UX — keeps
      // this page off N status counts.
      count: f.value === status ? totalCount : null,
    }));
  }, [status, totalCount]);

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-8">
        <AdminSectionTabs />

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="or-kicker text-or-orange-600">Admin · Comments</div>
            <h1 className="mt-1 font-display text-[22px] font-bold text-or-slate-900 sm:text-[26px]">
              Review moderation
            </h1>
            <p className="mt-1 max-w-xl text-[13px] text-or-slate-600">
              Approve, reject, or delete the comments readers leave on books. Star ratings
              themselves go live instantly and don't need approval.
            </p>
          </div>
        </div>

        {/* Status filter chips */}
        <div className="flex flex-wrap items-center gap-2">
          {statusCounts.map((filter) => (
            <button
              key={filter.value}
              type="button"
              onClick={() => applyStatus(filter.value)}
              aria-pressed={status === filter.value}
              className={
                status === filter.value
                  ? 'inline-flex items-center gap-2 rounded-full bg-or-slate-900 px-3.5 py-1.5 text-[13px] font-semibold text-white'
                  : 'inline-flex items-center gap-2 rounded-full border border-or-slate-200 bg-white px-3.5 py-1.5 text-[13px] font-semibold text-or-slate-700 hover:bg-or-slate-50'
              }
            >
              {filter.label}
              {filter.count !== null ? (
                <span
                  className={
                    status === filter.value
                      ? 'rounded-full bg-white/15 px-1.5 text-[11px]'
                      : 'rounded-full bg-or-slate-100 px-1.5 text-[11px] text-or-slate-600'
                  }
                >
                  {filter.count}
                </span>
              ) : null}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-2xl border border-or-slate-200 bg-white">
          <table className="w-full text-left text-[13px]">
            <thead className="bg-or-slate-50 text-[11px] uppercase tracking-[0.08em] text-or-slate-500">
              <tr>
                <th className="px-4 py-3">Reader</th>
                <th className="px-4 py-3">Book</th>
                <th className="px-4 py-3">Rating</th>
                <th className="px-4 py-3">Comment</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && reviews.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-or-slate-500">
                    Loading…
                  </td>
                </tr>
              ) : reviews.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-or-slate-500">
                    {status === 'pending'
                      ? 'Inbox zero — no comments awaiting approval.'
                      : 'No reviews match this filter.'}
                  </td>
                </tr>
              ) : (
                reviews.map((review) => (
                  <ReviewRow
                    key={review.id}
                    review={review}
                    busy={busyId === review.id}
                    onApprove={() => void handleModerate(review, 'approved')}
                    onReject={() => void handleModerate(review, 'rejected')}
                    onDelete={() => setPendingDelete(review)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalCount > PAGE_SIZE ? (
          <div className="flex items-center justify-between text-[12px] text-or-slate-500">
            <span>
              Page {page} of {lastPage} · {totalCount.toLocaleString()} reviews
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => goToPage(page + 1)}
                disabled={page >= lastPage}
              >
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => {
          if (!next) setPendingDelete(null);
        }}
        title="Delete this review?"
        description={
          pendingDelete
            ? `This permanently removes ${pendingDelete.readerName}'s review (including the star rating) from "${pendingDelete.bookTitle}". This can't be undone.`
            : ''
        }
        confirmLabel="Delete review"
        confirmVariant="danger"
        onConfirm={handleDelete}
      />
    </AppShell>
  );
}

interface ReviewRowProps {
  review: AdminReviewView;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
  onDelete: () => void;
}

function ReviewRow({ review, busy, onApprove, onReject, onDelete }: ReviewRowProps) {
  return (
    <tr className="border-t border-or-slate-100 align-top">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <ReaderAvatar
            name={review.readerName}
            avatarSlug={review.readerAvatarSlug}
            size="xs"
          />
          <span className="font-semibold text-or-slate-900">{review.readerName}</span>
        </div>
      </td>
      <td className="max-w-[200px] px-4 py-3">
        <div className="truncate font-semibold text-or-slate-900">{review.bookTitle}</div>
      </td>
      <td className="px-4 py-3">
        <StarRating value={review.rating} readOnly size="sm" />
      </td>
      <td className="max-w-[360px] px-4 py-3">
        <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-or-slate-700">
          {review.commentText}
        </p>
      </td>
      <td className="px-4 py-3">
        <StatusChip status={review.commentStatus} />
      </td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex items-center gap-1.5">
          {review.commentStatus !== 'approved' ? (
            <Button variant="primary" size="sm" onClick={onApprove} disabled={busy}>
              <Check className="h-3.5 w-3.5" aria-hidden />
              Approve
            </Button>
          ) : null}
          {review.commentStatus !== 'rejected' ? (
            <Button variant="secondary" size="sm" onClick={onReject} disabled={busy}>
              <X className="h-3.5 w-3.5" aria-hidden />
              Reject
            </Button>
          ) : null}
          <Button variant="danger" size="sm" onClick={onDelete} disabled={busy}>
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </div>
      </td>
    </tr>
  );
}

function StatusChip({ status }: { status: ReviewCommentStatus | null }) {
  if (status === 'approved') return <Chip variant="live">Approved</Chip>;
  if (status === 'pending') return <Chip variant="pending">Pending</Chip>;
  if (status === 'rejected') return <Chip variant="default">Rejected</Chip>;
  return <Chip variant="default">Rating only</Chip>;
}
